<script setup lang="ts">
import { ref, computed } from 'vue'
import Button from 'primevue/button'
import { useNavigation } from '@/composables/useNavigation'

interface Props {
  layout: 'mobile' | 'tablet' | 'desktop'
}

defineProps<Props>()

const { navItems, canSee, navigate, isActive } = useNavigation()

/**
 * Ítems destacados en la barra inferior. `useNavigation().navItems` es la lista
 * completa del menú; aquí solo se promote un subconjunto y el resto queda en el
 * hoja "Más".
 */
const primaryRoutes = ['home', 'quotes.index', 'customers.index']

const primaryItems = computed(() => navItems.value.filter(i => primaryRoutes.includes(i.route)))

/**
 * El Dashboard no forma parte de `navItems` (en `home` ya se entra), pero sí
 * necesita acceso directo en el móvil, igual que el perfil personal.
 */
const extraItems = computed(() => [
  { label: 'Dashboard', icon: 'pi pi-chart-line', route: 'dashboard', permission: 'dashboard.access' },
  { label: 'Perfil', icon: 'pi pi-cog', route: 'profile', permission: null },
])

const overflowItems = computed(() => [
  ...navItems.value.filter(i => !primaryRoutes.includes(i.route)),
  ...extraItems.value,
])

const showMenu = ref(false)

function open(item: { route: string | null }): void {
  navigate(item)
  showMenu.value = false
}
</script>

<template>
  <nav
    class="no-print fixed bottom-0 left-0 right-0 h-16 bg-white dark:bg-surface-900 border-t border-surface-200 dark:border-surface-700 z-[var(--z-sticky)] flex items-center justify-around px-2 safe-area-bottom"
    role="navigation"
    aria-label="Navegación principal móvil"
  >
    <template
      v-for="item in primaryItems"
      :key="item.route"
    >
      <!--
        Enlaces y no botones, por lo mismo que en el header y en la barra lateral:
        la barra de abajo es navegación, y un enlace se puede abrir en pestaña
        nueva, se puede compartir y ya responde a Enter y Espacio sin que haya
        que traducirlos a un clic.
      -->
      <router-link
        v-if="canSee(item)"
        :to="{ name: item.route }"
        class="flex flex-1 flex-col items-center gap-1 rounded-lg px-1 py-2 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        :class="[
          isActive(item.route)
            ? 'text-primary-700 dark:text-primary-300'
            : 'text-surface-500 hover:text-surface-800 dark:text-surface-400 dark:hover:text-surface-200',
        ]"
        :aria-current="isActive(item.route) ? 'page' : undefined"
      >
        <i
          :class="item.icon"
          class="text-lg"
          aria-hidden="true"
        />
        <span>{{ item.label }}</span>
      </router-link>
    </template>

    <Button
      icon="pi pi-ellipsis-h"
      label="Más"
      severity="text"
      class="flex-1 flex-col gap-1 py-2 px-1 text-xs text-surface-500 dark:text-surface-400"
      aria-haspopup="dialog"
      :aria-expanded="showMenu"
      @click="showMenu = true"
    />
  </nav>

  <Teleport to="body">
    <div
      v-if="showMenu"
      class="fixed inset-0 z-[var(--z-modal)] flex items-end sm:items-center justify-center p-4"
    >
      <div
        class="fixed inset-0 bg-black/50"
        aria-hidden="true"
        @click="showMenu = false"
      />
      <div
        class="relative bg-white dark:bg-surface-900 rounded-t-2xl sm:rounded-xl w-full max-w-sm sm:max-w-md shadow-xl animate-slide-up p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Más opciones"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
            Más opciones
          </h3>
          <Button
            icon="pi pi-times"
            severity="secondary"
            text
            size="small"
            aria-label="Cerrar"
            @click="showMenu = false"
          />
        </div>
        <div class="space-y-2">
          <template
            v-for="item in overflowItems"
            :key="item.route"
          >
            <Button
              v-if="canSee(item)"
              :icon="item.icon"
              :label="item.label"
              severity="text"
              class="w-full justify-start gap-3 py-3 text-base"
              @click="open(item)"
            />
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>